import React from 'react';
import { Customer, OnboardingTask, CustomerProgress, HealthScore } from '../../types';

interface QuickStatsProps {
  customer: Customer;
  tasks: OnboardingTask[];
  progress: CustomerProgress[];
  healthScore?: HealthScore | null;
  completedCount: number;
  totalCount: number;
}

const QuickStats: React.FC<QuickStatsProps> = ({ 
  customer, 
  tasks, 
  progress, 
  healthScore,
  completedCount,
  totalCount 
}) => {
  // Add validation and bounds checking for all calculations
  const validCompletedCount = Math.min(completedCount, totalCount);
  const completionPercentage = totalCount > 0 
    ? Math.min(100, Math.round((validCompletedCount / totalCount) * 100))
    : 0;
    
  const requiredTasks = tasks.filter(t => t.taskType === 'required');
  const completedRequired = progress.filter(p => 
    p.status === 'completed' && 
    requiredTasks.some(t => t.id === p.taskId)
  ).length;
  
  // Ensure required completed doesn't exceed available required tasks
  const validCompletedRequired = Math.min(completedRequired, requiredTasks.length);
  
  const getStatusIcon = () => {
    if (completionPercentage === 100) return '🎉';
    if (completionPercentage >= 75) return '🚀';
    if (completionPercentage >= 50) return '⚡';
    if (completionPercentage >= 25) return '🌱';
    return '🎯';
  };

  const getMotivationalMessage = () => {
    if (completionPercentage === 100) return "Congratulations! You've completed your onboarding journey!";
    if (completionPercentage >= 75) return "Almost there! Just a few more steps to go!";
    if (completionPercentage >= 50) return "Great progress! You're more than halfway there!";
    if (completionPercentage >= 25) return "Good start! Keep up the momentum!";
    return "Welcome! Let's begin your onboarding journey!";
  };

  const formatSegment = (segment: string): string => {
    return segment
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="quick-stats-container">
      <div className="stats-header">
        <div className="customer-info">
          <div className="customer-avatar">
            <span>{customer.companyName.charAt(0).toUpperCase()}</span>
          </div>
          <div className="customer-details">
            <h1 className="customer-name">Hi {customer.companyName}! {getStatusIcon()}</h1>
            <p className="motivational-message">{getMotivationalMessage()}</p>
          </div>
        </div>
        
        {healthScore && (
          <div className="health-score-badge">
            <div className={`score-circle score-${healthScore.score >= 80 ? 'high' : healthScore.score >= 60 ? 'medium' : 'low'}`}>
              <span className="score-value">{healthScore.score}</span>
              <span className="score-label">Health</span>
            </div>
          </div>
        )}
      </div>

      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-value">{completionPercentage}%</div>
            <div className="stat-label">Overall Progress</div>
          </div>
        </div>

        <div className="stat-card stat-completed">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-value">{validCompletedCount}/{totalCount}</div>
            <div className="stat-label">Tasks Completed</div>
          </div>
        </div>

        <div className="stat-card stat-required">
          <div className="stat-icon">🎯</div>
          <div className="stat-content">
            <div className="stat-value">{validCompletedRequired}/{requiredTasks.length}</div>
            <div className="stat-label">Required Done</div>
          </div>
        </div>

        <div className="stat-card stat-segment">
          <div className="stat-icon">🏆</div>
          <div className="stat-content">
            <div className="stat-value">{formatSegment(customer.segment)}</div>
            <div className="stat-label">Segment</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickStats;